// A part's 3D model (`PartModel3d`: a stored one, carried by the product and
// its copies, or a built body's, generated from its figures by
// renderer/builtModels.ts) turned into what the views draw: the 3D view's
// meshes (three/scene3d.ts) and the convex pieces the sections and a tilted plan
// project (renderer/partModel.ts). The one builder: every view of every model
// goes through it (docs/claude/tilt.md, *Parts with a 3D model*). Pure and
// DOM-free (test/partSolids.test.mjs).

import type {
  PartModel3d,
  PartSolid3d,
  Vec2,
} from './types';
import {
  convexMerge,
  earClip,
} from './triangulate';

export interface Pt3 { x: number; y: number; z: number }

/** A part's colour when neither it nor its model gives one (a role with none). */
export const PRODUCT_COLOR = '#b9c2cc';

/** A copy's own figures the model is drawn for: one whose width or depth is off
 *  the model's `size` by more than this (mm) draws its plain box. */
const FITS = 0.5;

/** The model a copy of `w` × `d` draws: its stored one, or null (none, or
 *  resized off the size it was made for: its box). */
export function fittedModel(m: PartModel3d | undefined, w: number, d: number): PartModel3d | null {
  if (!m || !Array.isArray(m.solids) || !m.solids.length || !Array.isArray(m.size)) return null;
  return fitsFootprint(w, d, m.size[0], m.size[1]) ? m : null;
}

/** Whether a copy of `w` × `d` is the footprint figures of `fw` × `fd` were made
 *  for (a stored model's, a duct fan's, a conveyor's, a doser's): within `FITS`. */
export function fitsFootprint(w: number, d: number, fw: number, fd: number): boolean {
  return Math.abs(fw - w) <= FITS && Math.abs(fd - d) <= FITS;
}

/** A sub-role's role: `body.side-l` is `body`'s (a solid of its own, painted
 *  apart, in its role's colour and finish); a plain role is its own. */
const parentRole = (role: string): string => role.split('.')[0];

/** A role's colour: `body` (and its sub-roles) the copy's own (`own`), else the
 *  model's for the role, then for its parent role; a role with none `fallback`. */
export function roleColour(m: PartModel3d, role: string, own: string | undefined, fallback: string): string {
  const parent = parentRole(role);
  if (parent === 'body' && own) return own;
  return m.colours?.[role] ?? m.colours?.[parent] ?? fallback;
}

/** Whether a role (or its parent role) is in one of the model's lists. */
const listed = (roles: readonly string[] | undefined, role: string): boolean => !!roles && (roles.includes(role) || roles.includes(parentRole(role)));

/** The model's solids by colour role, the roles in the order they first come:
 *  `metal` drawn as metal in 3D, `glass` as glass, `acrylic` as glossy acrylic,
 *  `water` as water (glass and water see-through in a section). */
export interface SolidGroup { role: string; colour: string; metal: boolean; glass: boolean; acrylic: boolean; water: boolean; solids: PartSolid3d[] }

export function solidGroups(m: PartModel3d, own: string | undefined, fallback: string): SolidGroup[] {
  const by = new Map<string, SolidGroup>();
  for (const s of m.solids) {
    let g = by.get(s.role);
    if (!g) {
      g = { role: s.role, colour: roleColour(m, s.role, own, fallback), metal: listed(m.metal, s.role), glass: listed(m.glass, s.role), acrylic: listed(m.acrylic, s.role), water: listed(m.water, s.role), solids: [] };
      by.set(s.role, g);
    }
    g.solids.push(s);
  }
  return [...by.values()];
}

/** `n` points round a circle of radius r about (cx, cy), counter-clockwise. */
export function circleRing(cx: number, cy: number, r: number, n = 32): Vec2[] {
  return Array.from({ length: n }, (_, i) => ({ x: cx + r * Math.cos((2 * Math.PI * i) / n), y: cy + r * Math.sin((2 * Math.PI * i) / n) }));
}

const rect = (x0: number, x1: number, y0: number, y1: number): Vec2[] => [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];

/** What the 3D view builds a solid as: a vertical prism (a box, a prism with its
 *  holes, a cylinder standing up), the convex hull of its corners, a body
 *  revolved about an axis along x of its frame — `turned`, the frame is the
 *  part's turned a quarter (its x the part's −y, its y the part's x: a body along
 *  y), so `W` maps it by `(q) => W({ x: q.y, y: -q.x })` —, or a tube of radius
 *  `r` along a centreline (a pipe's, `pipeCurve`; its end rings square to
 *  `ends`, the curve's own direction there). A cylinder's stations close
 *  both ends; a revolved body's are its own. */
export type SolidMesh =
  | { hull: Pt3[] }
  | { prism: { ring: Vec2[]; holes?: Vec2[][]; z0: number; z1: number } }
  | { revolve: { stations: Array<[number, number]>; axis: { y: number; z: number }; turned: boolean } }
  | { tube: { pts: Pt3[]; r: number; ends: [Pt3, Pt3] } };

/** Steps per span of a pipe's curve: the runs of its centreline, the 3D tube's
 *  and its pieces' alike (each a straight cylinder, which a section slices). */
const PIPE_STEPS = 8;

/** One coordinate of a centripetal Catmull-Rom span from x1 to x2 at `w` (0…1),
 *  its neighbours x0 and x3 and the knot gaps dt0…dt2 — three's
 *  `CatmullRomCurve3` (centripetal), which the doser's pipes were first drawn by. */
function catmullRom(x0: number, x1: number, x2: number, x3: number, dt0: number, dt1: number, dt2: number, w: number): number {
  const t1 = ((x1 - x0) / dt0 - (x2 - x0) / (dt0 + dt1) + (x2 - x1) / dt1) * dt1;
  const t2 = ((x2 - x1) / dt1 - (x3 - x1) / (dt1 + dt2) + (x3 - x2) / dt2) * dt1;
  const c2 = -3 * x1 + 3 * x2 - 2 * t1 - t2, c3 = 2 * x1 - 2 * x2 + t1 + t2;
  return x1 + t1 * w + c2 * w * w + c3 * w * w * w;
}

const sub3 = (p: Pt3, q: Pt3): Pt3 => ({ x: p.x - q.x, y: p.y - q.y, z: p.z - q.z });
const dot3 = (p: Pt3, q: Pt3): number => p.x * q.x + p.y * q.y + p.z * q.z;
const len3 = (p: Pt3): number => Math.hypot(p.x, p.y, p.z);
const unit3 = (p: Pt3): Pt3 => { const l = len3(p) || 1; return { x: p.x / l, y: p.y / l, z: p.z / l }; };
const cross3 = (p: Pt3, q: Pt3): Pt3 => ({ x: p.y * q.z - p.z * q.y, y: p.z * q.x - p.x * q.z, z: p.x * q.y - p.y * q.x });

/** A pipe's centreline: the smooth curve through its path's points — a
 *  centripetal Catmull-Rom, its ends' neighbours mirrored past them —, `steps`
 *  samples a span, the samples where it runs straight on left out (a pipe of
 *  two points is its two ends); and the curve's own direction at its two ends
 *  (`ends`, unit), which its end rings stand square to. */
function pipeCurve(path: ReadonlyArray<readonly [number, number, number]>, steps: number): { pts: Pt3[]; ends: [Pt3, Pt3] } {
  const P = path.map(([x, y, z]) => ({ x, y, z })), n = P.length;
  if (n < 2) return { pts: P, ends: [{ x: 0, y: 0, z: 1 }, { x: 0, y: 0, z: 1 }] };
  const mirror = (a: Pt3, b: Pt3): Pt3 => ({ x: 2 * a.x - b.x, y: 2 * a.y - b.y, z: 2 * a.z - b.z });
  const gap = (a: Pt3, b: Pt3): number => Math.pow(dot3(sub3(a, b), sub3(a, b)), 0.25);
  const out: Pt3[] = [P[0]];
  let first: Pt3 = sub3(P[1], P[0]), last: Pt3 = sub3(P[n - 1], P[n - 2]);
  for (let i = 0; i + 1 < n; i++) {
    const p0 = i > 0 ? P[i - 1] : mirror(P[0], P[1]), p1 = P[i], p2 = P[i + 1], p3 = i + 2 < n ? P[i + 2] : mirror(P[n - 1], P[n - 2]);
    let dt0 = gap(p0, p1), dt1 = gap(p1, p2), dt2 = gap(p2, p3);
    if (dt1 < 1e-4) dt1 = 1;
    if (dt0 < 1e-4) dt0 = dt1;
    if (dt2 < 1e-4) dt2 = dt1;
    for (let k = 1; k <= steps; k++) {
      const w = k / steps, at = (c: 'x' | 'y' | 'z'): number => catmullRom(p0[c], p1[c], p2[c], p3[c], dt0, dt1, dt2, w);
      out.push(k === steps ? p2 : { x: at('x'), y: at('y'), z: at('z') });
    }
    // The span's direction at its start (t1) and at its end (t2).
    const tan = (c: 'x' | 'y' | 'z', end: boolean): number => {
      const x0 = p0[c], x1 = p1[c], x2 = p2[c], x3 = p3[c];
      return end ? ((x2 - x1) / dt1 - (x3 - x1) / (dt1 + dt2) + (x3 - x2) / dt2) * dt1 : ((x1 - x0) / dt0 - (x2 - x0) / (dt0 + dt1) + (x2 - x1) / dt1) * dt1;
    };
    if (i === 0) first = { x: tan('x', false), y: tan('y', false), z: tan('z', false) };
    if (i + 2 === n) last = { x: tan('x', true), y: tan('y', true), z: tan('z', true) };
  }
  const pts = out.filter((p, i) => i === 0 || len3(sub3(p, out[i - 1])) > 1e-6);
  for (let k = pts.length - 2; k >= 1; k--) {
    const u = sub3(pts[k], pts[k - 1]), w = sub3(pts[k + 1], pts[k]);
    if (dot3(u, w) / (len3(u) * len3(w)) > 1 - 1e-9) pts.splice(k, 1);
  }
  return { pts, ends: [unit3(first), unit3(last)] };
}

/** How far a pipe's ring may stray inside its circle (mm): a thin pipe's has
 *  fewer points than a body's `round` (every one of them is sliced, a section
 *  at a time, and a doser's four pipes are a hundred runs). */
const PIPE_SAG = 0.25;

/** A pipe as straight cylinders along its centreline (`PIPE_STEPS` a span),
 *  each the corners of its two end rings: a ring where two runs meet stands
 *  across the mean of their directions, shared by both — no gap between them —
 *  and widened across the turn (a mitre: on both runs' cylinders, as the 3D
 *  view's tube, pipes3d.ts); an end ring square to the curve's own direction
 *  there; turned along with the centreline so the pipe never twists; its points
 *  as few as keep it within `PIPE_SAG` of its circle, at most `round`. */
function pipePieces(path: ReadonlyArray<readonly [number, number, number]>, r: number, round: number): Pt3[][] {
  const { pts: c, ends } = pipeCurve(path, PIPE_STEPS);
  if (c.length < 2 || r <= 0) return [];
  const sides = Math.min(round, Math.max(4, Math.ceil(Math.PI / Math.acos(Math.max(-1, 1 - PIPE_SAG / r)))));
  const seg = c.slice(1).map((p, i) => unit3(sub3(p, c[i])));
  const last = c.length - 1;
  const tan = c.map((_, i) => (i === 0 ? ends[0] : i === last ? ends[1] : unit3({ x: seg[i - 1].x + seg[i].x, y: seg[i - 1].y + seg[i].y, z: seg[i - 1].z + seg[i].z })));
  /** A normal square to `t`: `n` carried onto it, or any when it lies along `t`. */
  const square = (t: Pt3, n: Pt3): Pt3 => {
    const m = sub3(n, { x: t.x * dot3(n, t), y: t.y * dot3(n, t), z: t.z * dot3(n, t) });
    if (len3(m) > 1e-6) return unit3(m);
    return square(t, Math.abs(t.z) < 0.9 ? { x: 0, y: 0, z: 1 } : { x: 1, y: 0, z: 0 });
  };
  let nrm = square(tan[0], { x: 0, y: 0, z: 1 });
  const rings = c.map((o, i) => {
    nrm = square(tan[i], nrm);
    const bin = cross3(tan[i], nrm);
    // At a joint the ring is widened across the turn (`m`, in its plane) by the
    // half-angle, as pipes3d.ts `tubeMesh` widens it.
    const joint = i > 0 && i < last;
    const k = joint ? 1 / Math.max(0.3, dot3(seg[i - 1], tan[i])) : 1;
    const m0 = joint ? sub3(seg[i - 1], { x: tan[i].x * dot3(seg[i - 1], tan[i]), y: tan[i].y * dot3(seg[i - 1], tan[i]), z: tan[i].z * dot3(seg[i - 1], tan[i]) }) : null;
    const m = m0 && len3(m0) > 1e-9 ? unit3(m0) : null;
    return Array.from({ length: sides }, (_, j): Pt3 => {
      const u = (2 * Math.PI * j) / sides, a = r * Math.cos(u), b = r * Math.sin(u);
      const off = { x: a * nrm.x + b * bin.x, y: a * nrm.y + b * bin.y, z: a * nrm.z + b * bin.z };
      const w = m ? dot3(off, m) * (k - 1) : 0;
      return { x: o.x + off.x + (m ? m.x * w : 0), y: o.y + off.y + (m ? m.y * w : 0), z: o.z + off.z + (m ? m.z * w : 0) };
    });
  });
  return rings.slice(1).map((ring, i) => [...rings[i], ...ring]);
}

/** Stations along an axis in the frame `solidMesh` revolves in: along x as they
 *  are; along y (its frame turned a quarter, its x the part's −y) each t
 *  negated, their order reversed so they still run low to high. */
const alongMeshX = (axis: 'x' | 'y', st: ReadonlyArray<readonly [number, number]>): Array<[number, number]> =>
  axis === 'x' ? st.map(([t, r]): [number, number] => [t, r]) : st.map(([t, r]): [number, number] => [0 - t, r]).reverse();

export function solidMesh(s: PartSolid3d, round = 32): SolidMesh {
  switch (s.kind) {
    case 'box': return { prism: { ring: rect(s.x0, s.x1, s.y0, s.y1), z0: s.z0, z1: s.z1 } };
    case 'prism': {
      const ring = s.ring.map((p) => ({ x: p.x, y: p.y })), holes = (s.holes ?? []).filter((h) => h.length >= 3).map((h) => h.map((p) => ({ x: p.x, y: p.y })));
      return { prism: holes.length ? { ring, holes, z0: s.z0, z1: s.z1 } : { ring, z0: s.z0, z1: s.z1 } };
    }
    case 'hull': return { hull: corners(s.pts) };
    case 'cyl': {
      const [a, b] = s.at, lo = Math.min(s.from, s.to), hi = Math.max(s.from, s.to);
      if (s.axis === 'z') return { prism: { ring: circleRing(a, b, s.r, round), z0: lo, z1: hi } };
      return { revolve: { stations: alongMeshX(s.axis, [[lo, 0], [lo, s.r], [hi, s.r], [hi, 0]]), axis: { y: a, z: b }, turned: s.axis === 'y' } };
    }
    case 'revolve': return { revolve: { stations: alongMeshX(s.axis, s.stations), axis: { y: s.at[0], z: s.at[1] }, turned: s.axis === 'y' } };
    case 'pipe': {
      const { pts, ends } = pipeCurve(s.path, PIPE_STEPS);
      return { tube: { pts, r: s.r, ends } };
    }
  }
}

/** A ring's signed area (counter-clockwise > 0). */
const area = (r: readonly Vec2[]): number => {
  let s = 0;
  for (let i = 0; i < r.length; i++) { const p = r[i], q = r[(i + 1) % r.length]; s += p.x * q.y - q.x * p.y; }
  return s / 2;
};

function convex(r: readonly Vec2[]): boolean {
  let sign = 0;
  for (let i = 0; i < r.length; i++) {
    const a = r[i], b = r[(i + 1) % r.length], c = r[(i + 2) % r.length];
    const k = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(k) < 1e-9) continue;
    if (sign && Math.sign(k) !== sign) return false;
    sign = Math.sign(k);
  }
  return true;
}

const prismOf = (ring: readonly Vec2[], z0: number, z1: number): Pt3[] => [
  ...ring.map((p) => ({ x: p.x, y: p.y, z: z0 })),
  ...ring.map((p) => ({ x: p.x, y: p.y, z: z1 })),
];

/** A circle of `n` points about an axis along x (`along` 'x': at x = t, centre
 *  (y, z) = (a, b)) or along y (at y = t, centre (x, z) = (a, b)). */
function roundAbout(along: 'x' | 'y', t: number, a: number, b: number, r: number, n: number): Pt3[] {
  return Array.from({ length: n }, (_, i) => {
    const u = (2 * Math.PI * i) / n, c = a + r * Math.cos(u), z = b + r * Math.sin(u);
    return along === 'x' ? { x: t, y: c, z } : { x: c, y: t, z };
  });
}

/** A hull's corners as points. */
const corners = (pts: ReadonlyArray<readonly [number, number, number]>): Pt3[] => pts.map(([x, y, z]) => ({ x, y, z }));

/** Whether points span a volume: not all on one plane. */
function solid3(ps: readonly Pt3[]): boolean {
  if (ps.length < 4) return false;
  const [a] = ps, sub = (p: Pt3, q: Pt3): Pt3 => ({ x: p.x - q.x, y: p.y - q.y, z: p.z - q.z });
  const b = ps.find((p) => Math.hypot(p.x - a.x, p.y - a.y, p.z - a.z) > 1e-6);
  if (!b) return false;
  const u = sub(b, a);
  const n = ps.map((p) => { const v = sub(p, a); return { x: u.y * v.z - u.z * v.y, y: u.z * v.x - u.x * v.z, z: u.x * v.y - u.y * v.x }; }).find((c) => Math.hypot(c.x, c.y, c.z) > 1e-6);
  return !!n && ps.some((p) => Math.abs(n.x * (p.x - a.x) + n.y * (p.y - a.y) + n.z * (p.z - a.z)) > 1e-6);
}

/** A solid as convex pieces (each its corners) in the part's frame: a box one,
 *  a cylinder one (its two end circles), a hull one (its corners), a prism its
 *  ring's convex parts less its holes, a revolved body a frustum between each two
 *  stations along it — one at a single t (an open end's disc) its flat circle,
 *  the widest —, a pipe a straight cylinder per run of its centreline
 *  (`pipePieces`). None for a solid with no volume (but that disc). */
export function solidPieces3(s: PartSolid3d, round = 32): Pt3[][] {
  switch (s.kind) {
    case 'box':
      return s.x1 - s.x0 > 1e-9 && s.y1 - s.y0 > 1e-9 && s.z1 - s.z0 > 1e-9 ? [prismOf(rect(s.x0, s.x1, s.y0, s.y1), s.z0, s.z1)] : [];
    case 'hull': {
      const ps = corners(s.pts);
      return solid3(ps) ? [ps] : [];
    }
    case 'prism': {
      if (s.z1 - s.z0 <= 1e-9 || s.ring.length < 3 || Math.abs(area(s.ring)) < 1e-9) return [];
      const holes = (s.holes ?? []).filter((h) => h.length >= 3);
      const rings = !holes.length && convex(s.ring) ? [s.ring] : convexMerge(earClip(s.ring, holes));
      return rings.map((r) => prismOf(r, s.z0, s.z1));
    }
    case 'revolve': {
      const [a, b] = s.at, st = s.stations, out: Pt3[][] = [];
      for (let i = 0; i + 1 < st.length; i++) {
        const [t0, r0] = st[i], [t1, r1] = st[i + 1];
        if (Math.abs(t1 - t0) > 1e-9 && Math.max(r0, r1) > 1e-9) out.push([...roundAbout(s.axis, t0, a, b, r0, round), ...roundAbout(s.axis, t1, a, b, r1, round)]);
      }
      if (out.length || !st.length) return out;
      const [t, r] = st.reduce((w, q) => (q[1] > w[1] ? q : w));
      return r > 1e-9 ? [roundAbout(s.axis, t, a, b, r, round)] : [];
    }
    case 'cyl': {
      const lo = Math.min(s.from, s.to), hi = Math.max(s.from, s.to), [a, b] = s.at;
      if (hi - lo <= 1e-9 || s.r <= 0) return [];
      if (s.axis === 'z') return [prismOf(circleRing(a, b, s.r, round), lo, hi)];
      return [[...roundAbout(s.axis, lo, a, b, s.r, round), ...roundAbout(s.axis, hi, a, b, s.r, round)]];
    }
    case 'pipe': return pipePieces(s.path, s.r, round);
  }
}

/** The model's own copy: a copy never shares one with its product or another copy. */
export function cloneModel3d(m: PartModel3d | undefined): PartModel3d | undefined {
  return m ? (JSON.parse(JSON.stringify(m)) as PartModel3d) : undefined;
}
